import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Megaphone, Minimize2, Radio, Users2 } from "lucide-react";
import { useDebateStore } from "../../store/debateStore";
import {
  selectConsensusGauge,
  selectCurrentTurn,
} from "../../store/selectors";
import { usePersonaTheme } from "../../hooks/usePersonaTheme";
import { useLipsyncRef } from "../../hooks/useLipsync";
import {
  AvatarAllocationProvider,
  useResolvedAvatarUrl,
} from "../orchestrator/AvatarAllocationContext";
import { RoleIcon, roleLabel } from "../common/RoleIcon";
import type { Persona } from "../../domain/types";

/**
 * Sentinel speaker id used by `ServerTtsEngine` for the council's closing
 * verdict read-out. The narrator is a synthetic speaker — it has no
 * persona record — so the spotlight branches on this id and renders a
 * dedicated "Konsey Sözcüsü" card instead of attempting a `personas`
 * lookup that would silently fall through to the idle placeholder.
 *
 * MUST stay in lock-step with `NARRATOR_SPEAKER_ID` in
 * `components/audio/ServerTtsEngine.tsx`.
 */
const NARRATOR_SPEAKER_ID = "__council_narrator__";

/* ============================================================================
 * PresentModeShell — "Agentic Theatre" cinematic HUD overlay.
 *
 * Architecture (after the 2026 redesign):
 *   • Single source of truth per metric. The bottom `LiveMetricsBar` (rendered
 *     by ConsensusModePage) owns all numerical state — turn count, consensus
 *     percentage, elapsed timer, token estimate, etc. This shell deliberately
 *     does NOT mirror them, eliminating the duplicate-data bug where the HUD
 *     and the metrics strip showed inconsistent values.
 *   • Right transcript column boundary is respected via the
 *     `--present-right-col-w` CSS variable so the topic banner never bleeds
 *     under the transcript card.
 *   • The speaker spotlight is the visual focal point — large persona display
 *     with audio-reactive FFT ring, persona-themed chromatic glow, and an
 *     organic 5-bar waveform that activates only while audible.
 *
 * Design references: LiveKit Aura, OpenVoiceUI face system, Apple Vision Pro
 * floating panels, Westworld terminal HUD.
 * ========================================================================== */

const TOPOLOGY_LABELS: Record<string, string> = {
  "round-robin": "Sıralı Tartışma",
  "cross-fire": "Çapraz Ateş",
  "moderator-led": "Moderatör Yönetimli",
  "diverse-poles": "Karşıt Kutuplar",
  "solo-then-debate": "Önce Solo / Sonra Tartışma",
};

/* Lazy-load the WebGL/Canvas FFT visualizer so non-present routes pay no cost. */
const FFTRadialVisualizer = lazy(() =>
  import("../audio/FFTRadialVisualizer").then((m) => ({
    default: m.FFTRadialVisualizer,
  })),
);

/* ─────────────────────────────────────────────────────────────────────────
 * Wall-clock time — replaces the duplicate elapsed timer from the old HUD.
 * Updates once per second; stops when the document is hidden.
 * ──────────────────────────────────────────────────────────────────────── */
function useWallClock(): string {
  const [now, setNow] = useState<string>(() =>
    new Date().toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
  );

  useEffect(() => {
    const tick = () =>
      setNow(
        new Date().toLocaleTimeString("tr-TR", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  return now;
}

/* ─────────────────────────────────────────────────────────────────────────
 * CinematicFrame — corner brackets + scanline + ambient consensus glow.
 * Lifted into its own component so we can compose / disable it cleanly.
 * ──────────────────────────────────────────────────────────────────────── */
function CinematicFrame({ pct }: { pct: number }) {
  const ringColor =
    pct >= 85 ? "152, 60%, 50%" : pct >= 65 ? "38, 90%, 55%" : "215, 70%, 55%";

  return (
    <>
      {/* Ambient consensus-driven inner glow (subtle, breathes with score). */}
      <div
        className="pointer-events-none absolute inset-0 z-[0]"
        style={{
          boxShadow: `inset 0 0 120px -40px hsl(${ringColor} / 0.10), inset 0 0 2px 0 hsl(${ringColor} / 0.18)`,
          transition: "box-shadow 1.2s ease",
        }}
        aria-hidden
      />

      {/* Cinematic letterbox — dark gradient at top/bottom edges. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 via-black/20 to-transparent z-[1]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 via-black/25 to-transparent z-[1]"
        aria-hidden
      />

      {/* Scanline drift. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden z-[1]" aria-hidden>
        <div
          className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.07] to-transparent"
          style={{ animation: "scanline 8s linear infinite" }}
        />
      </div>

      {/*
       * Corner brackets removed (2026-05).
       * The iconic "scope" L-shaped frames in the four corners caused visual
       * clutter against the persona spotlight + transcript panel and the
       * product owner explicitly requested their removal. The cinematic feel
       * is preserved by the letterbox gradients, scanline, side vignettes,
       * and ambient consensus glow above.
       */}

      {/* Side vignettes for focus. */}
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black/30 to-transparent z-[1]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-black/30 to-transparent z-[1]"
        aria-hidden
      />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * NarratorSpotlight — dedicated card for the council's closing verdict
 * read-out. The narrator is a synthetic broadcast-anchor voice with no
 * persona record, so we render a chrome-tinted card with a megaphone
 * glyph instead of a persona avatar. Visual contract:
 *
 *   • Chrome/amber tint — distinct from any persona theme so the audience
 *     instantly registers the tonal shift "now the council is announcing
 *     its decision".
 *   • Audio-reactive halo + 5-bar waveform — matches the speaker card
 *     vocabulary so the live-status affordance is consistent.
 *   • Title fixed to "Konsey Sözcüsü" with the subtitle "Karar Açıklaması".
 * ──────────────────────────────────────────────────────────────────────── */
function NarratorSpotlight() {
  const audibleSpeakerId = useDebateStore((s) => s.audibleSpeakerId);
  const isAudible = audibleSpeakerId === NARRATOR_SPEAKER_ID;
  const narratorGlow = "38, 92%, 58%"; // warm amber — broadcast-anchor accent

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="council-narrator"
        initial={{ opacity: 0, x: -16, scale: 0.96 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: 16, scale: 0.96 }}
        transition={{ duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
        className="relative flex items-center gap-4 rounded-2xl border border-white/[0.10] bg-gradient-to-br from-black/85 via-black/75 to-black/85 backdrop-blur-2xl px-4 py-2.5"
        style={{
          boxShadow: `0 12px 40px -12px rgb(0 0 0 / 0.75), 0 0 0 1px hsl(${narratorGlow} / 0.22)`,
        }}
      >
        <div
          className="pointer-events-none absolute inset-x-3 top-0 h-px"
          style={{
            background: `linear-gradient(to right, transparent, hsl(${narratorGlow} / 0.55), transparent)`,
          }}
          aria-hidden
        />

        <div className="relative flex-shrink-0">
          <div
            className="pointer-events-none absolute inset-0 -m-2 rounded-full"
            style={{
              background: `radial-gradient(circle at center, hsl(${narratorGlow} / 0.35) 0%, transparent 65%)`,
              animation: isAudible
                ? "speaking-halo-outer 1.6s ease-in-out infinite"
                : "idle-breathe 4.2s ease-in-out infinite",
            }}
            aria-hidden
          />
          <div
            className="relative grid h-10 w-10 place-items-center rounded-full ring-2"
            style={{
              background: `linear-gradient(135deg, hsl(${narratorGlow} / 0.55) 0%, hsl(${narratorGlow} / 0.18) 100%)`,
              borderColor: `hsl(${narratorGlow} / 0.7)`,
              boxShadow: `0 4px 16px hsl(${narratorGlow} / 0.45)`,
              ["--tw-ring-color" as string]: `hsl(${narratorGlow} / 0.45)`,
            }}
          >
            <Megaphone className="h-4 w-4 text-white" strokeWidth={2.2} aria-hidden />
          </div>
        </div>

        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-1.5">
            <span
              className="relative flex h-1.5 w-1.5"
              aria-label={isAudible ? "Karar okunuyor" : "Hazırlanıyor"}
            >
              <span
                className="absolute inline-flex h-full w-full rounded-full opacity-70"
                style={{
                  background: isAudible ? "hsl(152 78% 50%)" : `hsl(${narratorGlow})`,
                  animation: "ping 1.6s cubic-bezier(0,0,0.2,1) infinite",
                }}
              />
              <span
                className="relative inline-flex h-1.5 w-1.5 rounded-full"
                style={{
                  background: isAudible ? "hsl(152 78% 55%)" : `hsl(${narratorGlow})`,
                  boxShadow: `0 0 6px ${
                    isAudible ? "hsl(152 78% 55% / 0.8)" : `hsl(${narratorGlow} / 0.7)`
                  }`,
                }}
              />
            </span>
            <span className="text-[9px] font-semibold uppercase tracking-[0.24em] text-white/55">
              Konsey Açıklaması
            </span>
          </div>
          <span className="mt-0.5 text-[15px] font-semibold text-white leading-tight truncate max-w-[280px]">
            Konsey Sözcüsü
          </span>
          <span className="text-[10px] font-medium text-white/55 leading-tight truncate max-w-[280px]">
            Karar Açıklaması
          </span>
        </div>

        <div className="ml-1 hidden items-end gap-[3px] h-7 sm:flex" aria-hidden>
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className="block w-[3px] rounded-full transition-opacity"
              style={{
                background: `hsl(${narratorGlow})`,
                opacity: isAudible ? 0.85 : 0.18,
                height: isAudible ? "100%" : "30%",
                animation: isAudible
                  ? `wave-organic-${(i % 5) + 1} ${0.9 + i * 0.12}s ease-in-out infinite`
                  : "none",
                animationDelay: `${i * 80}ms`,
              }}
            />
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * useTurnPosition — derives "Tur N/M · Söz K/T" coordinates from the
 * canonical store. Round is 1-based for human display; round cap comes
 * from the negotiated session value. Söz numarası = position of the
 * current turn in the chronological turnOrder; total = turnOrder length
 * (committed turns only — pending entries are skipped by the store).
 * ──────────────────────────────────────────────────────────────────────── */
function useTurnPosition(): {
  round: number;
  maxRounds: number;
  turn: number;
  totalTurns: number;
} {
  const roundIndex = useDebateStore((s) => s.roundIndex);
  const maxRounds = useDebateStore((s) => s.maxRounds ?? 0);
  const turnOrder = useDebateStore((s) => s.turnOrder);
  const currentTurnId = useDebateStore((s) => s.currentTurnId);

  const turn = useMemo(() => {
    if (currentTurnId) {
      const idx = turnOrder.indexOf(currentTurnId);
      if (idx >= 0) return idx + 1;
    }
    // Between turns: report the most-recently-committed position so the
    // counter doesn't flicker back to 0 during the transcript hand-off.
    return turnOrder.length;
  }, [turnOrder, currentTurnId]);

  return {
    round: Math.max(1, roundIndex + 1),
    maxRounds: Math.max(1, maxRounds),
    turn,
    totalTurns: turnOrder.length,
  };
}

/* ─────────────────────────────────────────────────────────────────────────
 * NarratorBottomLeft — anchors the existing `NarratorSpotlight` card at
 * the same bottom-left slot that `BigSpeakerStage` uses, but ONLY while
 * the synthetic narrator voice is the audible speaker. The narrator
 * card has its own chrome (megaphone glyph + warm amber accent) so the
 * audience instantly registers the tonal shift "council debate ended,
 * now the verdict is being announced."
 *
 * Why a wrapper instead of inlining the position into NarratorSpotlight
 *   The wrapper keeps `NarratorSpotlight` agnostic of WHERE it lives,
 *   so the same card can be re-anchored later (e.g. dashboard preview,
 *   spectator HUD) without splitting its visual contract. The
 *   wrapper owns ONLY the absolute positioning + visibility gate.
 * ──────────────────────────────────────────────────────────────────────── */
export function NarratorBottomLeft() {
  const audibleSpeakerId = useDebateStore((s) => s.audibleSpeakerId);
  if (audibleSpeakerId !== NARRATOR_SPEAKER_ID) return null;
  return (
    <div
      className="pointer-events-none absolute z-[12] flex flex-col items-center"
      style={{ left: "24px", bottom: "96px" }}
      aria-label="Konsey sözcüsü sahnesi"
    >
      <div className="pointer-events-auto">
        <NarratorSpotlight />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * SpeakerSpotlight + SpeakerAvatarDisc retired (2026-05).
 *
 * The top-left "Şu An Konuşan" hero card was the third place the
 * audience saw the active speaker's identity (after the rim avatar
 * halo and the BigSpeakerStage portrait). Operators flagged the
 * triplication as visual noise and asked us to free the top-bar
 * real estate so the topic question can occupy the full canvas
 * width. The card was removed; the `useTurnPosition` hook above is
 * reused by `BigSpeakerStage` for its small Tur/Söz subtitle, so
 * it stays. Git history preserves the previous implementation if
 * the chrome is ever needed again (commit-find: SpeakerSpotlight
 * + SpeakerAvatarDisc).
 * ──────────────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────────────
 * Scroll-to-turn IPC — `BigSpeakerStage` and `RealTimeTranscript` live in
 * separate React subtrees and we don't want to leak transcript state into
 * the global Zustand store just to support a click-to-locate gesture.
 *
 * The lightweight contract: `BigSpeakerStage` dispatches a `CustomEvent`
 * on `window` with the target `turnId` and the transcript subscribes
 * with a passive listener that resolves the index and calls
 * `virtualizer.scrollToIndex`. This:
 *   • avoids any new global state (transcript stays render-pure),
 *   • survives hot-reloads (no stale subscriptions),
 *   • is self-DRY (only one event name, see `CONSENSUS_SCROLL_TURN_EVENT`).
 *
 * Event name MUST stay in lock-step with the listener registered in
 * `components/transcript/RealTimeTranscript.tsx`.
 */
const CONSENSUS_SCROLL_TURN_EVENT = "consensus:scroll-to-turn";

function dispatchScrollToTurn(turnId: string | null | undefined): void {
  if (!turnId || typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(CONSENSUS_SCROLL_TURN_EVENT, { detail: { turnId } }),
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * BigSpeakerStage — large (200 px) audio-reactive portrait card pinned to
 * the BOTTOM-LEFT corner of the cinematic canvas, clear of the transcript
 * column AND the bottom LiveMetricsBar. Renders as a free-floating
 * "broadcast camera" on the active speaker:
 *
 *   • 200 px circular portrait with persona-themed glow halo.
 *   • Lipsync-driven jaw scale + brightness on the portrait (`.lip-jaw`).
 *   • Lipsync-driven box-shadow pulse on the disc frame (`.lip-pulse`).
 *   • FFT-driven 16-bar radial visualizer ring around the portrait
 *     (lazy-mounted only while audio is pumping — keeps the analyser
 *     RAF cheap during silence).
 *   • Floating identity bar below: persona name, expertise, role,
 *     TUR / SÖZ live counter.
 *   • CLICK-TO-LOCATE: tapping the card scrolls the right-rail
 *     transcript to the active speaker's current turn so operators
 *     can read what they're saying without hunting the virtual list.
 *
 * Positioning rationale (2026-05 rev):
 *   The previous anchor (right-mid, leaning on `--present-right-col-w`)
 *   silently overlapped the transcript on dense viewports because the
 *   transcript card has its OWN inset padding that the CSS variable
 *   doesn't account for. Pinning to `left: 24px; bottom: 96px` puts
 *   the card in the orchestrator's bottom-left vacant zone — the
 *   round table is centred and never reaches that corner, the
 *   ModeratorBar sits ABOVE the LiveMetricsBar so neither is touched,
 *   and the transcript column is on the opposite side of the canvas.
 *   No overlap is possible regardless of viewport width or topology.
 *
 * Renders nothing when no speaker is active (idle state) or when the
 * narrator (synthetic verdict voice) is speaking — the existing
 * `NarratorSpotlight` already owns the chrome for that case.
 * ──────────────────────────────────────────────────────────────────────── */
export function BigSpeakerStage() {
  const currentTurn = useDebateStore(selectCurrentTurn);
  const personas = useDebateStore((s) => s.personas);
  const audibleSpeakerId = useDebateStore((s) => s.audibleSpeakerId);
  const { round, maxRounds, turn } = useTurnPosition();

  const speakerId = audibleSpeakerId || currentTurn?.speakerId;
  const persona: Persona | null = speakerId ? personas[speakerId] ?? null : null;
  const themeStyle = usePersonaTheme(persona);
  const isAudible = !!audibleSpeakerId && audibleSpeakerId === persona?.id;
  const lipRef = useLipsyncRef<HTMLDivElement>(isAudible);
  const pulseRef = useLipsyncRef<HTMLDivElement>(isAudible);
  // Hooks must run unconditionally — pass a sentinel persona when idle so
  // the rules-of-hooks invariant holds. The actual render is gated below.
  const resolvedUrl = useResolvedAvatarUrl(
    persona ?? ({ id: "__idle__" } as unknown as Persona),
  );

  // Hide for idle, narrator (own card), or missing persona.
  if (!persona || speakerId === NARRATOR_SPEAKER_ID) return null;

  const initials = persona.displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleScrollToCurrentTurn = () => {
    if (currentTurn?.id) dispatchScrollToTurn(currentTurn.id);
  };

  return (
    <div
      className="pointer-events-none absolute z-[12] flex flex-col items-center gap-3"
      style={{
        // Bottom-left vacant zone — guaranteed clear of:
        //   • transcript column (right side of the canvas),
        //   • LiveMetricsBar (`bottom: 0`, ~64px tall),
        //   • ModeratorBar (sits inside the orchestrator card above
        //     the metrics bar — does not bleed below it),
        //   • round table (auto-centred in the orchestrator card,
        //     never reaches the canvas corner).
        left: "24px",
        bottom: "96px",
        ...(themeStyle as Record<string, string | number>),
      }}
      aria-label="Aktif konuşmacı sahnesi"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={persona.id}
          initial={{ opacity: 0, scale: 0.86, y: 18 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: -12 }}
          transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
          className="pointer-events-auto relative flex flex-col items-center cursor-pointer group focus-visible:outline-none"
          role="button"
          tabIndex={0}
          onClick={handleScrollToCurrentTurn}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleScrollToCurrentTurn();
            }
          }}
          title={`Transkriptte ${persona.displayName}'in son konuşmasına git`}
          aria-label={`Transkripte git: ${persona.displayName}`}
        >
          {/* === OUTER HALO RING — amplitude-driven radial bloom ===
             A second ring sits just outside the portrait disc; its
             opacity + scale are wired to `--lip-amp` so during silence
             it settles to a calm idle pulse, and during voiced syllables
             it brightens / breathes outward. */}
          <div
            ref={pulseRef}
            className="lip-halo pointer-events-none absolute inset-0 -m-6 rounded-full"
            style={{
              background:
                "radial-gradient(circle at center, hsl(var(--persona-glow) / 0.55) 0%, hsl(var(--persona-glow) / 0.10) 55%, transparent 75%)",
              animation: isAudible
                ? undefined
                : "idle-breathe 4.6s ease-in-out infinite",
            }}
            aria-hidden
          />

          {/* === FFT RADIAL VISUALIZER ===
             Lazy-loaded canvas that draws 16 amplitude bars around the
             portrait. Mounts only while audio is actually pumping so
             the analyser RAF stays cheap on idle screens. */}
          {isAudible ? (
            <Suspense fallback={null}>
              <div className="pointer-events-none absolute inset-0 -m-4 h-[232px] w-[232px]">
                <FFTRadialVisualizer
                  size={232}
                  color="hsl(var(--persona-glow))"
                />
              </div>
            </Suspense>
          ) : null}

          {/* === PORTRAIT DISC — 200 px, lipsync-coupled === */}
          <div
            className="lip-pulse relative grid h-[200px] w-[200px] place-items-center overflow-hidden rounded-full ring-[3px] shadow-[0_24px_64px_-16px_rgba(0,0,0,0.9)]"
            style={{
              background:
                "linear-gradient(135deg, hsl(var(--persona-glow) / 0.45) 0%, hsl(var(--persona-glow) / 0.12) 100%)",
              borderColor: "hsl(var(--persona-glow) / 0.85)",
              ["--tw-ring-color" as string]: "hsl(var(--persona-glow) / 0.55)",
            }}
          >
            {resolvedUrl ? (
              <div ref={lipRef} className="lip-jaw absolute inset-0">
                <img
                  src={resolvedUrl}
                  alt={persona.displayName}
                  loading="eager"
                  draggable={false}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    // Defensive: stale URL → collapse to initials gradient
                    // by hiding the broken portrait. Allocator already
                    // clamps backend stamps to POOL_SIZE; this is a
                    // belt-and-braces guard for missing on-disk fixtures.
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            ) : (
              <div ref={lipRef} className="lip-jaw grid place-items-center">
                <span className="font-mono text-[64px] font-bold tracking-tight text-white drop-shadow-[0_6px_20px_rgba(0,0,0,0.7)]">
                  {initials}
                </span>
              </div>
            )}

            {/* Bottom inner gradient — guarantees contrast between the
               portrait and the identity bar's text descender. */}
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 to-transparent"
              aria-hidden
            />

            {/* Role badge — bottom-right of disc. */}
            <span
              className="absolute -bottom-1 -right-1 z-10 grid h-9 w-9 place-items-center rounded-full bg-black ring-[3px] ring-black/85"
              style={{ borderColor: "hsl(var(--persona-glow))" }}
              title={roleLabel(persona.role)}
            >
              <RoleIcon
                role={persona.role}
                className="h-5 w-5"
                style={{ color: "hsl(var(--persona-glow))" }}
              />
            </span>

            {/* LIVE indicator — top-left of disc, only while audible. */}
            {isAudible ? (
              <div className="absolute left-2 top-2 z-10 flex items-center gap-1 rounded-full bg-black/70 px-1.5 py-0.5 backdrop-blur-md">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-80" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-rose-500" />
                </span>
                <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-rose-200">
                  Canlı
                </span>
              </div>
            ) : null}
          </div>

          {/* === IDENTITY BAR — name + expertise + tur/söz === */}
          <div className="mt-5 max-w-[260px] rounded-2xl border border-white/[0.10] bg-black/72 backdrop-blur-2xl px-4 py-2.5 shadow-[0_12px_36px_-10px_rgba(0,0,0,0.75)]">
            <div className="flex items-center justify-center gap-1.5">
              <span className="text-[8px] font-bold uppercase tracking-[0.24em] text-white/50">
                Şu An Konuşan
              </span>
              <span
                className="font-mono text-[9px] font-semibold tabular-nums text-white/70"
                aria-label={`Tur ${round} bölü ${maxRounds}, söz numarası ${turn}`}
              >
                T{round}/{maxRounds} · S{turn}
              </span>
            </div>
            <div className="mt-1 text-center text-[15px] font-bold leading-tight text-white truncate">
              {persona.displayName}
            </div>
            {persona.expertise ? (
              <div className="mt-0.5 text-center text-[11px] font-medium leading-snug text-white/55 line-clamp-2">
                {persona.expertise}
              </div>
            ) : null}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * SpeakerQueueRail — next 3 personas, displayed as initials chips with the
 * persona theme color for instant visual recognition. Hover reveals name.
 * ──────────────────────────────────────────────────────────────────────── */
function SpeakerQueueRail() {
  const personas = useDebateStore((s) => s.personas);
  const turnOrder = useDebateStore((s) => s.turnOrder);
  const turnsById = useDebateStore((s) => s.turnsById);

  const queue = useMemo(() => {
    const pending = turnOrder
      .map((id) => turnsById[id])
      .filter((t) => t && !t.endedAt)
      .slice(0, 3);
    return pending
      .map((t) => personas[t!.speakerId])
      .filter((p): p is Persona => Boolean(p));
  }, [turnOrder, turnsById, personas]);

  if (queue.length === 0) return null;

  return (
    <div className="flex items-center gap-2 rounded-xl border border-white/[0.06] bg-black/55 backdrop-blur-xl px-3 py-1.5 shadow-[0_4px_20px_-8px_rgb(0_0_0/0.6)]">
      <Users2 className="h-3 w-3 text-white/40" aria-hidden />
      <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-white/45">
        Sırada
      </span>
      <div className="flex -space-x-1.5">
        {queue.map((p, i) => {
          const initial = p.displayName
            .split(/\s+/)
            .filter(Boolean)[0]!.charAt(0)
            .toUpperCase();
          return (
            <div
              key={p.id}
              className="grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold text-white ring-2 ring-black/80"
              style={{
                background: `linear-gradient(135deg, ${p.themeColor}, color-mix(in srgb, ${p.themeColor} 40%, black))`,
                zIndex: 3 - i,
                opacity: 1 - i * 0.18,
              }}
              title={p.displayName}
            >
              {initial}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * TopicBanner — constrained to the LEFT (orchestrator) column so it never
 * overlaps the right transcript panel. Uses the
 * `--present-right-col-w` CSS variable set by the parent layout.
 *
 * Overflow strategy (the screenshot bug fix):
 *   The banner sits in a fixed slot whose height/width is bounded by the
 *   surrounding layout, so a long question like "İran, ABD ve İsrail
 *   arasındaki savaşın mevcut durumunun Türkiye üzerinde oluşturduğu ve
 *   oluşturabileceği risklere yönelik çözüm faaliyetleri geliştirin."
 *   would otherwise either:
 *     (a) be silently clipped by `line-clamp-2` with no way to read the
 *         tail, or
 *     (b) blow the layout out vertically, pushing the speaker stage
 *         under the right panel.
 *
 *   Defense in depth:
 *     1. **Auto-fit font** — text size shrinks for long inputs so a
 *        2-line clamp can hold ≈260 chars instead of ≈140.
 *     2. **Click-to-expand** — operator can tap the banner to reveal
 *        the FULL question; banner grows in place with a smooth height
 *        animation, capped by `max-h-[40vh]` + internal scroll so we
 *        still don't push the stage off-screen for an essay-length
 *        prompt.
 *     3. **Hover tooltip** — `title` attr always carries the full
 *        text so a quick mouse-over reveals everything without
 *        committing a click.
 *     4. **Visible chevron** — small affordance icon tells the user
 *        the banner IS interactive (since fixed banners often look
 *        decorative).
 *     5. **Pointer events scoped** — parent shell uses
 *        `pointer-events-none` so HUD chrome doesn't eat clicks; the
 *        banner re-enables them on its own root only.
 * ──────────────────────────────────────────────────────────────────── */
function TopicBanner() {
  const query = useDebateStore((s) => s.query);
  const status = useDebateStore((s) => s.status);
  const [expanded, setExpanded] = useState(false);

  if (!query) return null;

  const statusMeta: { label: string; tone: string } =
    status === "running"
      ? { label: "Müzakere Devam Ediyor", tone: "text-emerald-300/95 border-emerald-400/40 bg-emerald-500/[0.08]" }
      : status === "finalized"
      ? { label: "Karar Verildi", tone: "text-violet-300/95 border-violet-400/40 bg-violet-500/[0.08]" }
      : status === "provisioning"
      ? { label: "Konsey Hazırlanıyor", tone: "text-amber-300/95 border-amber-400/40 bg-amber-500/[0.08]" }
      : { label: "Hazırlık", tone: "text-white/75 border-white/15 bg-white/[0.04]" };

  // Auto-fit font: longer questions get smaller type so the 2-line
  // clamp continues to hold the bulk of the prompt without overflow.
  // Thresholds tuned against the present-mode banner's effective width
  // (~820px max, ~600px typical) at the chosen leading-snug line-height.
  const len = query.length;
  const fontSize =
    len > 320 ? "text-[11.5px] leading-[1.45]" :
    len > 220 ? "text-[12.5px] leading-snug" :
    len > 140 ? "text-[13px] leading-snug" :
                "text-[14px] leading-snug";

  // The 2-line clamp will mostly fit ≈140-260 chars depending on font
  // size. Anything beyond that warrants the expand affordance even if
  // the user never clicks — they at least know there's MORE text.
  const isTruncatable = len > 140;

  return (
    <div
      className="pointer-events-none fixed z-[56] px-4"
      style={{
        top: "0.75rem",
        // Konsey Cognitive Renaissance — Bölüm B5 (revize 2026-05).
        // Eski 280px sağ margin, banner alanını 304px'e düşürüyordu —
        // soru "İra A..." gibi 5 karakterde kesiliyordu. Yeni değer:
        //   left  = 1.5rem (sabit, sol kenardan boşluk)
        //   right = transcript col + 96px (saat + ESC chip için)
        // Banner artık ekran üstünde merkezi geniş bir bant — soru
        // çok kısa kesilmez.
        left: "1.5rem",
        right: "calc(var(--present-right-col-w, min(34vw, 600px)) + 96px)",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -12, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
        layout
        className="pointer-events-auto relative mx-auto max-w-[820px] overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-r from-black/85 via-black/72 to-black/85 backdrop-blur-2xl shadow-[0_16px_56px_-16px_rgb(0_0_0/0.8)]"
      >
        {/* Top accent shimmer. */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/40 to-transparent"
          aria-hidden
        />

        <button
          type="button"
          onClick={isTruncatable ? () => setExpanded((v) => !v) : undefined}
          aria-expanded={expanded}
          aria-label={isTruncatable ? (expanded ? "Soruyu kısalt" : "Tam soruyu göster") : undefined}
          title={isTruncatable && !expanded ? query : undefined}
          className={
            "block w-full text-left transition-colors " +
            (isTruncatable ? "hover:bg-white/[0.02] cursor-pointer" : "cursor-default")
          }
        >
          <div className="flex items-start gap-3.5 px-5 py-2.5">
            {/* Status badge. */}
            <div
              className={`mt-0.5 flex flex-shrink-0 items-center gap-1.5 rounded-md border px-2 py-0.5 ${statusMeta.tone}`}
            >
              <span className="relative flex h-1.5 w-1.5">
                <span
                  className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
                  style={{ background: "currentColor" }}
                />
                <span
                  className="relative inline-flex h-1.5 w-1.5 rounded-full"
                  style={{ background: "currentColor" }}
                />
              </span>
              <span className="text-[9px] font-bold uppercase tracking-[0.2em]">
                {statusMeta.label}
              </span>
            </div>

            <div className="mt-1 h-4 w-px bg-white/15 flex-shrink-0" aria-hidden />

            {/* Topic question — clamped or expanded. */}
            <p
              className={
                `min-w-0 flex-1 font-semibold text-white/95 [text-wrap:balance] ${fontSize} ` +
                (expanded
                  ? "max-h-[40vh] overflow-y-auto pr-2 [scrollbar-width:thin]"
                  : "line-clamp-2")
              }
            >
              {query}
            </p>

            {/* Expand affordance — only rendered when truncation is
                possible so short questions don't pretend to be
                interactive. */}
            {isTruncatable && (
              <div className="mt-0.5 flex-shrink-0 self-center">
                <span
                  className={
                    "inline-flex h-6 w-6 items-center justify-center rounded-full border border-white/15 bg-white/[0.04] text-white/70 transition-transform " +
                    (expanded ? "rotate-180" : "")
                  }
                  aria-hidden
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 4.5l3 3 3-3" />
                  </svg>
                </span>
              </div>
            )}
          </div>
        </button>
      </motion.div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Main shell.
 * ──────────────────────────────────────────────────────────────────────── */
export function PresentModeShell() {
  const presentMode = useDebateStore((s) => s.presentMode);
  const setPresentMode = useDebateStore((s) => s.setPresentMode);
  const topology = useDebateStore((s) => s.topology);
  const personasMap = useDebateStore((s) => s.personas);
  const { scalarScore } = useDebateStore(selectConsensusGauge);
  const reduceMotion = useReducedMotion();
  const wallClock = useWallClock();
  const pct = Math.round(scalarScore * 100);

  /* Council snapshot for AvatarAllocationProvider — `assignUniqueAvatars`
   * needs the FULL council to guarantee per-debate uniqueness AND to clamp
   * any stale `avatar_url` stamps (e.g. from a debate provisioned during
   * the deprecated 20-slot pool era) back into the live 15-slot range.
   * Memoised on identity-set + stamp+gender+honorific signature so the
   * map is rebuilt only when the council actually changes. */
  const councilPersonas = useMemo(
    () => Object.values(personasMap),
    [personasMap],
  );

  /* Body-level toggle so global CSS can hide chrome (header, callouts, etc). */
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (presentMode) {
      document.body.setAttribute("data-present-mode", "true");
    } else {
      document.body.removeAttribute("data-present-mode");
    }
    return () => {
      document.body.removeAttribute("data-present-mode");
    };
  }, [presentMode]);

  return (
    <AnimatePresence>
      {presentMode ? (
        <AvatarAllocationProvider personas={councilPersonas}>
        <motion.div
          key="present-hud"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduceMotion ? 0.01 : 0.32, ease: "easeOut" }}
          className="pointer-events-none fixed inset-0 z-[55]"
          aria-label="Sunum modu yer paylaşımı"
        >
          {/* === Cinematic frame (vignettes, scanline, brackets, ambient glow) === */}
          <CinematicFrame pct={pct} />

          {/* ────────────────────────────────────────────────────────────
           * TOP HUD
           *   • Left  : (empty — TopicBanner now claims the full top
           *             bar; the SpeakerSpotlight hero card was retired
           *             in 2026-05 because the round-table avatar +
           *             BigSpeakerStage portrait + CenterPhaseBoard
           *             already cover "who is speaking" three different
           *             ways. The duplicated mini-card stole top-bar
           *             real estate that should belong to the topic
           *             question.)
           *   • Right : Wall-clock + Speaker queue + ESC
           * (Topic banner is rendered separately below, full-width.)
           * ─────────────────────────────────────────────────────────── */}
          <div className="pointer-events-auto absolute inset-x-0 top-0 z-[2] flex items-start justify-end gap-4 px-4 pt-3 sm:px-5">
            <div className="flex items-center gap-2">
              <SpeakerQueueRail />

              {/* Wall-clock — replaces the duplicated elapsed timer that used
                  to live up here. The actual elapsed timer / consensus / turn
                  / participant counters are owned by LiveMetricsBar at bottom. */}
              <div className="flex items-center gap-2 rounded-xl border border-white/[0.06] bg-black/55 backdrop-blur-xl px-3 py-1.5 shadow-[0_4px_20px_-8px_rgb(0_0_0/0.6)]">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-70" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.75)]" />
                </span>
                <span className="font-mono text-xs font-semibold tabular-nums text-white/95 tracking-wider">
                  {wallClock}
                </span>
              </div>

              <button
                type="button"
                onClick={() => setPresentMode(false)}
                className="focus-ring inline-flex items-center gap-1.5 rounded-xl border border-white/[0.08] bg-black/55 backdrop-blur-xl px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/60 hover:text-white hover:border-white/25 hover:bg-black/70 transition-all shadow-[0_4px_20px_-8px_rgb(0_0_0/0.6)]"
                aria-label="Sunum modundan çık"
                title="ESC tuşu ile de çıkabilirsiniz"
              >
                <Minimize2 className="h-3 w-3" aria-hidden />
                <span className="hidden sm:inline">ESC</span>
              </button>
            </div>
          </div>

          {/* === Topic question banner (constrained to left column) === */}
          <TopicBanner />

          {/* Konsey Cognitive Renaissance — Bölüm B2.
              Önceki sürümde `BigSpeakerStage` ve `NarratorBottomLeft`
              fixed `z-[12]` overlay olarak burada render ediliyordu.
              ConsensusModePage'in akış-içi LiveMetricsBar'ı (z-auto)
              ile stacking-context çakışıp metrics bar'ı eziyordu.
              Çözüm: BigSpeaker artık LiveMetricsBar'ın **içinde**
              (`<BigSpeakerCompactSlot />`); aynı parent → çakışma yok.
              Bu yüzden burada hiçbir şey render etmiyoruz. */}

          {/* ────────────────────────────────────────────────────────────
           * BOTTOM HUD
           *   • Left   : DAI Consensus brand mark (subtle)
           *   • Center : LIVE indicator + topology
           *   • Right  : Press-P hint
           * (No metrics here — they live in the LiveMetricsBar inline.)
           * ─────────────────────────────────────────────────────────── */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] flex items-center justify-between px-4 pb-2 sm:px-5">
            {/* Brand mark. */}
            <div className="flex items-center gap-2.5 opacity-55">
              <div className="grid h-6 w-6 place-items-center rounded-md bg-gradient-to-br from-indigo-500/85 via-violet-500/85 to-fuchsia-500/85 shadow-[0_4px_12px_-2px_rgba(139,92,246,0.45)]">
                <span className="font-black text-[9px] text-white tracking-tight">D</span>
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/55">
                  DAI Consensus
                </span>
                <span className="text-[8px] font-medium uppercase tracking-[0.16em] text-white/30">
                  Agentic Theatre
                </span>
              </div>
            </div>

            {/* LIVE + topology — centered floating capsule. */}
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3 rounded-full border border-white/[0.08] bg-black/72 backdrop-blur-2xl px-4 py-1.5 shadow-[0_8px_28px_-8px_rgb(0_0_0/0.7)]">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-65" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.85)]" />
              </span>
              <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-red-300/95">
                Canlı
              </span>
              <div className="h-3 w-px bg-white/15" aria-hidden />
              <Radio className="h-3 w-3 text-white/55" aria-hidden />
              <span className="text-[10px] font-semibold text-white/65 uppercase tracking-[0.16em]">
                {TOPOLOGY_LABELS[topology] ?? topology}
              </span>
            </div>

            {/* ESC hint (subtle, non-interactive — actual button is top-right). */}
            <div className="hidden items-center gap-2 sm:flex">
              <kbd className="font-mono text-[9px] font-semibold uppercase tracking-wider text-white/40 px-1.5 py-0.5 rounded border border-white/[0.08] bg-white/[0.02]">
                P
              </kbd>
              <span className="text-[9px] font-medium uppercase tracking-[0.18em] text-white/35">
                Sunum modu
              </span>
            </div>
          </div>
        </motion.div>
        </AvatarAllocationProvider>
      ) : null}
    </AnimatePresence>
  );
}

/* ============================================================================
 * BigSpeakerCompactSlot — yatay layout, LiveMetricsBar İÇİNE entegre.
 *
 * Konsey Cognitive Renaissance — Bölüm B2.
 *
 * Önceki `BigSpeakerStage` `fixed` overlay olarak `z-[12]` katmanında
 * yaşıyordu; PresentModeShell'in `z-[55] fixed` overlay'i de altında
 * kalan akış-içi `LiveMetricsBar` (z-auto) ile çakışıyordu —
 * stacking context iki ayrı element grubunu birleştiremediği için
 * BigSpeaker disc fizyolojik olarak metrics bar'ı eziyordu.
 *
 * Çözüm: BigSpeaker'ı `LiveMetricsBar`'ın **içine** akış-içi olarak
 * yerleştirmek. Aynı parent → aynı stacking context → çakışma yok.
 *
 * Boyut farkı (BigSpeakerStage'e göre):
 *   • Disc 200 → 140 px
 *   • FFT visualizer 232 → 168 px
 *   • Identity bar disc'in **sağında** (yatay layout, dikey değil)
 *
 * Click-to-locate scroll-to-turn aynen çalışır (dispatchScrollToTurn).
 * Narrator branch için aynı slot içinde küçük chrome amber badge.
 *
 * Export edilir → ConsensusModePage'in LiveMetricsBar'ı bunu render eder.
 * ========================================================================== */
export function BigSpeakerCompactSlot() {
  const currentTurn = useDebateStore(selectCurrentTurn);
  const personas = useDebateStore((s) => s.personas);
  const audibleSpeakerId = useDebateStore((s) => s.audibleSpeakerId);

  const speakerId = audibleSpeakerId || currentTurn?.speakerId;
  const isNarrator = speakerId === NARRATOR_SPEAKER_ID;
  const persona: Persona | null =
    !isNarrator && speakerId ? personas[speakerId] ?? null : null;
  const themeStyle = usePersonaTheme(persona);
  const isAudible =
    (!!audibleSpeakerId && persona && audibleSpeakerId === persona.id) ||
    isNarrator;
  const lipRef = useLipsyncRef<HTMLDivElement>(!!isAudible);
  // Hooks must run unconditionally — sentinel persona when idle.
  const resolvedUrl = useResolvedAvatarUrl(
    persona ?? ({ id: "__idle__" } as unknown as Persona),
  );

  // Idle / no speaker → boş slot (LiveMetricsBar layout korunur).
  if (!isNarrator && !persona) {
    return (
      <div
        className="flex shrink-0 items-center gap-3 px-4 py-2 min-w-[280px]"
        aria-label="Konuşmacı bekleniyor"
      >
        <div className="grid h-[64px] w-[64px] place-items-center rounded-full bg-white/[0.04] ring-1 ring-white/[0.08]">
          <Megaphone className="h-5 w-5 text-white/30" aria-hidden />
        </div>
        <div className="flex flex-col">
          <span className="text-[9px] uppercase tracking-[0.22em] text-white/40 font-semibold">
            Bekleniyor
          </span>
          <span className="text-xs font-semibold text-white/60 leading-tight">
            Konsey Hazırlanıyor
          </span>
        </div>
      </div>
    );
  }

  const handleScrollToCurrentTurn = () => {
    if (currentTurn?.id) dispatchScrollToTurn(currentTurn.id);
  };

  // Narrator chrome — synthetic verdict reader. Persona yok, megaphone glyph.
  if (isNarrator) {
    const narratorGlow = "38, 92%, 58%";
    return (
      <div
        className="flex shrink-0 items-center gap-3 px-4 py-2 min-w-[280px]"
        style={{ background: `linear-gradient(90deg, hsl(${narratorGlow} / 0.06), transparent)` }}
        aria-label="Konsey sözcüsü canlı"
      >
        <div
          className="relative grid h-[72px] w-[72px] place-items-center rounded-full ring-2"
          style={{
            background: `linear-gradient(135deg, hsl(${narratorGlow} / 0.55) 0%, hsl(${narratorGlow} / 0.18) 100%)`,
            borderColor: `hsl(${narratorGlow} / 0.7)`,
            ["--tw-ring-color" as string]: `hsl(${narratorGlow} / 0.55)`,
          }}
        >
          <Megaphone className="h-7 w-7 text-white" strokeWidth={2.2} aria-hidden />
          <span
            className="pointer-events-none absolute inset-0 -m-2 rounded-full"
            style={{
              background: `radial-gradient(circle at center, hsl(${narratorGlow} / 0.35) 0%, transparent 65%)`,
              animation: "speaking-halo-outer 1.6s ease-in-out infinite",
            }}
            aria-hidden
          />
        </div>
        <div className="flex flex-col">
          <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-amber-300/95">
            Konsey Açıklaması · Canlı
          </span>
          <span className="text-sm font-semibold text-white leading-tight">
            Konsey Sözcüsü
          </span>
          <span className="text-[10px] text-white/55 leading-tight">
            Karar Açıklaması
          </span>
        </div>
      </div>
    );
  }

  // persona kesin var → tam UI
  const initials = (persona as Persona).displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      className="flex shrink-0 items-center gap-3 px-4 py-2 min-w-[280px]"
      style={themeStyle as Record<string, string | number>}
      aria-label="Aktif konuşmacı kompakt"
    >
      <button
        type="button"
        onClick={handleScrollToCurrentTurn}
        className="group relative shrink-0 cursor-pointer focus-visible:outline-none"
        title={`Transkriptte ${(persona as Persona).displayName}'in son konuşmasına git`}
        aria-label={`Transkripte git: ${(persona as Persona).displayName}`}
      >
        {/* FFT visualizer (audible only) */}
        {isAudible ? (
          <Suspense fallback={null}>
            <div className="pointer-events-none absolute inset-0 -m-2 h-[80px] w-[80px]">
              <FFTRadialVisualizer size={80} color="hsl(var(--persona-glow))" />
            </div>
          </Suspense>
        ) : null}
        {/* Disc */}
        <div
          className="relative grid h-[64px] w-[64px] place-items-center overflow-hidden rounded-full ring-2 transition-transform group-hover:scale-105"
          style={{
            background:
              "linear-gradient(135deg, hsl(var(--persona-glow) / 0.45) 0%, hsl(var(--persona-glow) / 0.12) 100%)",
            borderColor: "hsl(var(--persona-glow) / 0.85)",
            ["--tw-ring-color" as string]: "hsl(var(--persona-glow) / 0.55)",
          }}
        >
          {resolvedUrl ? (
            <div ref={lipRef} className="lip-jaw absolute inset-0">
              <img
                src={resolvedUrl}
                alt={(persona as Persona).displayName}
                draggable={false}
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          ) : (
            <span className="font-mono text-[20px] font-bold tracking-tight text-white">
              {initials}
            </span>
          )}
          {/* LIVE indicator */}
          {isAudible && (
            <span className="absolute left-1 top-1 z-10 flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-80" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
            </span>
          )}
        </div>
      </button>
      {/* Identity right of disc */}
      <div className="flex min-w-0 flex-col">
        <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-white/55">
          Şu An Konuşan
        </span>
        <span className="truncate text-sm font-semibold text-white leading-tight max-w-[180px]">
          {(persona as Persona).displayName}
        </span>
        {(persona as Persona).expertise ? (
          <span className="truncate text-[10px] text-white/55 leading-tight max-w-[180px]">
            {(persona as Persona).expertise}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/* ============================================================================
 * Toggle button — used in the AppHeader. Unchanged in behaviour, polished in
 * motion (subtle haptic-like press).
 * ========================================================================== */
export function PresentModeToggle() {
  const presentMode = useDebateStore((s) => s.presentMode);
  const togglePresentMode = useDebateStore((s) => s.togglePresentMode);
  return (
    <button
      type="button"
      onClick={togglePresentMode}
      className="focus-ring inline-flex h-8 items-center gap-1.5 rounded-full border border-line-soft/60 bg-surface-2/60 px-3 text-[11px] font-medium uppercase tracking-wider text-fg-muted hover:border-[hsl(var(--accent)/0.45)] hover:text-fg-strong transition-colors"
      aria-pressed={presentMode}
      title="Sunum modu (P)"
    >
      {presentMode ? (
        <Minimize2 className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <svg
          className="h-3.5 w-3.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
        </svg>
      )}
      Sunum
    </button>
  );
}
